import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameweekLeaderboard } from '../hooks/useFantasyData';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import '../styles/FantasyLeaderboard.css';

const FantasyLeaderboardPage = ({ setActiveTab }) => {
  const { t } = useTranslation();
  const [season] = useState(2024);
  const [gameweekNumber, setGameweekNumber] = useState(1);
  const [viewMode, setViewMode] = useState('gameweek'); // 'gameweek' or 'season'

  const { leaderboard, loading, error } = useGameweekLeaderboard(gameweekNumber, season);

  const handlePrevGameweek = () => {
    if (gameweekNumber > 1) {
      setGameweekNumber(gameweekNumber - 1);
    }
  };

  const handleNextGameweek = () => {
    if (gameweekNumber < 38) {
      setGameweekNumber(gameweekNumber + 1);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error} />;
  if (!leaderboard) return <ErrorAlert message={t('fantasy.leaderboard.no_data')} />;

  const entries = leaderboard.entries || [];

  const getRoleColor = (role) => {
    const colors = {
      'P': '#1f77b4', // blue
      'D': '#2ca02c', // green
      'C': '#ff7f0e', // orange
      'A': '#d62728'  // red
    };
    return colors[role] || '#777777';
  };

  return (
    <div className="fantasy-leaderboard-page container mt-4">
      <button
        className="btn-back-header mb-3"
        onClick={() => setActiveTab(9)}
        aria-label="Back to Fantasy main"
        title="Back to Fantasy"
      >
        ← Back
      </button>
      <h1 className="mb-4">{t('fantasy.leaderboard.title')}</h1>

      {viewMode === 'gameweek' && (
        <div className="gameweek-navigation mb-4">
          <button
            className="btn btn-primary"
            onClick={handlePrevGameweek}
            disabled={gameweekNumber === 1}
          >
            {t('fantasy.leaderboard.previous')}
          </button>

          <div className="gameweek-info">
            <h2>
              {t('fantasy.leaderboard.gameweek')} {gameweekNumber} / 38
            </h2>
            <p className="season-info">{t('fantasy.leaderboard.season')}: {season}</p>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleNextGameweek}
            disabled={gameweekNumber === 38}
          >
            {t('fantasy.leaderboard.next')}
          </button>
        </div>
      )}

      <div className="leaderboard-stats mb-4">
        <div className="stat-info">
          <span>{t('fantasy.leaderboard.total_players')}: {leaderboard.totalPlayers}</span>
          <span>|</span>
          <span>{t('fantasy.leaderboard.displayed')}: {entries.length}</span>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped leaderboard-table">
          <thead className="table-dark">
            <tr>
              <th className="rank-col">#</th>
              <th>{t('fantasy.leaderboard.player')}</th>
              <th className="role-col">{t('fantasy.leaderboard.role')}</th>
              <th>{t('fantasy.leaderboard.team')}</th>
              <th className="points-col">{t('fantasy.leaderboard.fantasy_points')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={`${entry.playerId}-${entry.rank}`} className="leaderboard-row">
                <td className="rank-col">
                  <span className="rank-badge">{entry.rank}</span>
                </td>
                <td className="player-name">
                  {entry.playerName}
                </td>
                <td className="role-col">
                  <span
                    className="role-badge"
                    style={{ backgroundColor: getRoleColor(entry.role) }}
                  >
                    {entry.role}
                  </span>
                </td>
                <td>{entry.teamName}</td>
                <td className="points-col highlight">
                  {entry.fantasyPoints}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {entries.length === 0 && (
        <div className="alert alert-info mt-4">
          {t('fantasy.leaderboard.no_entries')}
        </div>
      )}
    </div>
  );
};

export default FantasyLeaderboardPage;
